<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, vue/html-closing-bracket-newline */
import axios from 'axios';
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
} from 'vue';
import { useRoute } from 'vue-router';

const route = useRoute();
const capture = ref(null);
const loading = ref(true);
const submitting = ref(false);
const submitted = ref(false);
const errorMessage = ref('');
const conversationId = ref(null);
const interactionDone = ref(false);
const customFrame = ref(null);
const injectedNodes = [];
const originalTitle = document.title;

const fieldMetadata = {
  fullName: { label: 'Nome completo', type: 'text', autocomplete: 'name' },
  phone: { label: 'Telefone', type: 'tel', autocomplete: 'tel' },
  cpf: { label: 'CPF', type: 'text', autocomplete: 'off' },
  email: { label: 'E-mail', type: 'email', autocomplete: 'email' },
  birthDate: {
    label: 'Data de nascimento',
    type: 'date',
    autocomplete: 'bday',
  },
};

const form = reactive({
  fullName: '',
  phone: '',
  cpf: '',
  email: '',
  birthDate: '',
  website: '',
});

const enabledFields = computed(() =>
  (capture.value?.form_fields || [])
    .filter(field => field.enabled)
    .map(field => ({ ...field, ...fieldMetadata[field.key] }))
);

const pageStyle = computed(() => {
  const design = capture.value?.design || {};
  return {
    '--capture-primary': design.primary_color || '#2563eb',
    '--capture-secondary': design.secondary_color || '#0f172a',
    '--capture-background': design.background_color || '#f1f5f9',
    '--capture-card': design.card_color || '#ffffff',
    '--capture-text': design.text_color || '#0f172a',
  };
});

const content = computed(() => capture.value?.content || {});
const isCustom = computed(() => capture.value?.editor_mode === 'custom');
const showInteraction = computed(
  () => capture.value?.experience !== 'form' && !interactionDone.value
);
const interaction = computed(() => {
  if (capture.value?.experience === 'roulette') {
    return {
      marker: 'GIRE',
      title: 'Gire e libere sua análise',
      description:
        'Você ganhou atendimento prioritário para consultar crédito.',
      action: 'Girar roleta',
    };
  }
  return {
    marker: 'RASPE',
    title: 'Raspe para revelar seu benefício',
    description: 'Análise gratuita liberada para seus dados.',
    action: 'Raspar benefício',
  };
});

const escapeHtml = value =>
  String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

const attribution = () => {
  const query = new URLSearchParams(window.location.search);
  return Object.fromEntries(
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']
      .map(key => [key, query.get(key)])
      .filter(([, value]) => value)
  );
};

const appendHeadNode = node => {
  document.head.appendChild(node);
  injectedNodes.push(node);
  return node;
};

const upsertMeta = (name, value) => {
  if (!value) return;
  const node = document.createElement('meta');
  node.name = name;
  node.content = value;
  appendHeadNode(node);
};

const loadExternalScript = src => {
  if (!src || document.querySelector(`script[src="${src}"]`)) return;
  const script = document.createElement('script');
  script.async = true;
  script.src = src;
  appendHeadNode(script);
};

const configureSeo = () => {
  const seo = capture.value.seo || {};
  document.title = seo.title || capture.value.name;
  upsertMeta('description', seo.description);
  upsertMeta('keywords', seo.keywords);
  if (seo.noindex) upsertMeta('robots', 'noindex,nofollow');
  if (seo.canonical_url) {
    const canonical = document.createElement('link');
    canonical.rel = 'canonical';
    canonical.href = seo.canonical_url;
    appendHeadNode(canonical);
  }
};

const configureTracking = () => {
  const tracking = capture.value.tracking || {};
  const googleId = tracking.ga4_id || tracking.google_ads_id;
  if (googleId) {
    window.dataLayer = window.dataLayer || [];
    window.gtag =
      window.gtag ||
      function gtag() {
        window.dataLayer.push(arguments);
      };
    window.gtag('js', new Date());
    if (tracking.ga4_id) window.gtag('config', tracking.ga4_id);
    if (tracking.google_ads_id) window.gtag('config', tracking.google_ads_id);
    loadExternalScript(
      `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(googleId)}`
    );
  }
  if (tracking.gtm_id) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
    loadExternalScript(
      `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(tracking.gtm_id)}`
    );
  }
  if (tracking.meta_pixel_id) {
    window.fbq =
      window.fbq ||
      function fbq() {
        if (window.fbq.callMethod) {
          window.fbq.callMethod.apply(window.fbq, arguments);
        } else {
          window.fbq.queue.push(arguments);
        }
      };
    window.fbq.queue = window.fbq.queue || [];
    window.fbq.loaded = true;
    window.fbq.version = '2.0';
    loadExternalScript('https://connect.facebook.net/en_US/fbevents.js');
    window.fbq('init', tracking.meta_pixel_id);
    window.fbq('track', 'PageView');
  }
};

const trackLead = () => {
  const tracking = capture.value.tracking || {};
  if (window.gtag) {
    window.gtag('event', 'generate_lead');
    if (tracking.google_ads_id && tracking.google_ads_label) {
      window.gtag('event', 'conversion', {
        send_to: `${tracking.google_ads_id}/${tracking.google_ads_label}`,
      });
    }
  }
  if (window.fbq) window.fbq('track', 'Lead');
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'talkvia_lead_submitted',
      capture: capture.value.slug,
    });
  }
};

const notifyCustomFrame = payload => {
  customFrame.value?.contentWindow?.postMessage(
    { type: 'talkvia:lead-result', ...payload },
    '*'
  );
};

const submitLead = async customData => {
  if (submitting.value || submitted.value) return;
  submitting.value = true;
  errorMessage.value = '';
  try {
    const data =
      customData ||
      Object.fromEntries(
        enabledFields.value.map(field => [field.key, form[field.key]])
      );
    const response = await axios.post(
      `/public/api/v1/talkvia_lead_captures/${route.params.slug}/submit`,
      {
        data,
        website: form.website,
        referer: document.referrer,
        attribution: attribution(),
      }
    );
    submitted.value = true;
    conversationId.value = response.data.conversation_id;
    trackLead();
    notifyCustomFrame({
      success: true,
      message: response.data.message,
      conversationId: response.data.conversation_id,
    });
  } catch (error) {
    errorMessage.value =
      error.response?.data?.error || 'Não foi possível enviar seus dados.';
    notifyCustomFrame({ success: false, message: errorMessage.value });
  } finally {
    submitting.value = false;
  }
};

const customFormHtml = computed(() => {
  const fields = enabledFields.value
    .map(
      field => `<label class="talkvia-field">${escapeHtml(field.label)}
        <input name="${field.key}" type="${field.type}" autocomplete="${field.autocomplete}" ${field.required ? 'required' : ''}>
      </label>`
    )
    .join('');
  return `<form data-talkvia-standard-form>
    <h2>${escapeHtml(content.value.form_title)}</h2>
    ${fields}
    <input name="website" tabindex="-1" autocomplete="off" class="talkvia-honeypot">
    <button type="submit">${escapeHtml(content.value.button_label)}</button>
    <p data-talkvia-status aria-live="polite"></p>
  </form>`;
});

const customDocument = computed(() => {
  if (!capture.value) return '';
  const design = capture.value.design || {};
  const suppliedHtml = capture.value.custom_html || '';
  const html = suppliedHtml.includes('data-talkvia-form')
    ? suppliedHtml.replace(
        /<[^>]*data-talkvia-form[^>]*>(?:<\/[^>]+>)?/i,
        customFormHtml.value
      )
    : `${suppliedHtml}${customFormHtml.value}`;
  const bridge = `
    const form = document.querySelector('[data-talkvia-standard-form]');
    const status = document.querySelector('[data-talkvia-status]');
    form?.addEventListener('submit', event => {
      event.preventDefault();
      const values = Object.fromEntries(new FormData(form).entries());
      const website = values.website || '';
      delete values.website;
      status.textContent = 'Enviando...';
      parent.postMessage({ type: 'talkvia:lead-submit', data: values, website }, '*');
    });
    window.addEventListener('message', event => {
      if (event.source !== parent || event.data?.type !== 'talkvia:lead-result') return;
      status.textContent = event.data.message || '';
      if (event.data.success) form?.reset();
      document.dispatchEvent(new CustomEvent('talkvia:submitted', { detail: event.data }));
    });`;
  return `<!doctype html><html lang="pt-BR"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
    <style>
      :root{--primary:${escapeHtml(design.primary_color)};--secondary:${escapeHtml(design.secondary_color)};--background:${escapeHtml(design.background_color)};--card:${escapeHtml(design.card_color)};--text:${escapeHtml(design.text_color)}}
      *{box-sizing:border-box}body{margin:0;min-height:100vh;background:var(--background);color:var(--text);font-family:Inter,ui-sans-serif,system-ui,sans-serif}body>section{padding:48px 24px;text-align:center;background:var(--secondary);color:white}body>section h1{font-size:clamp(2rem,5vw,4rem);margin:0 0 12px}body>section p{font-size:1.05rem;opacity:.82}form{width:min(92%,480px);margin:40px auto;padding:28px;border-radius:18px;background:var(--card);box-shadow:0 20px 50px #0f172a22}form h2{text-align:center;margin:0 0 22px}.talkvia-field{display:grid;gap:7px;margin:0 0 14px;font-size:14px;font-weight:650}.talkvia-field input{height:48px;border:1px solid #cbd5e1;border-radius:10px;padding:0 14px;font:inherit;background:white;color:#0f172a}.talkvia-field input:focus{outline:3px solid color-mix(in srgb,var(--primary) 22%,transparent);border-color:var(--primary)}button{width:100%;min-height:48px;border:0;border-radius:10px;background:var(--primary);color:white;font-weight:750;cursor:pointer}.talkvia-honeypot{position:absolute!important;left:-10000px!important}[data-talkvia-status]{min-height:20px;text-align:center;font-size:13px;margin:12px 0 0}
    </style></head><body>${html}<script>${bridge}<${'/script'}><script>${capture.value.custom_js || ''}<${'/script'}></body></html>`;
});

const handleCustomMessage = event => {
  if (
    event.source !== customFrame.value?.contentWindow ||
    event.data?.type !== 'talkvia:lead-submit'
  )
    return;
  form.website = event.data.website || '';
  submitLead(event.data.data || {});
};

const loadCapture = async () => {
  try {
    const { data } = await axios.get(
      `/public/api/v1/talkvia_lead_captures/${route.params.slug}`
    );
    capture.value = data;
    configureSeo();
    configureTracking();
    await nextTick();
  } catch {
    errorMessage.value = 'Esta página de captação não está disponível.';
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  window.addEventListener('message', handleCustomMessage);
  loadCapture();
});

onBeforeUnmount(() => {
  window.removeEventListener('message', handleCustomMessage);
  injectedNodes.forEach(node => node.remove());
  document.title = originalTitle;
});
</script>

<template>
  <main
    class="capture-page"
    :class="capture ? `capture-page--${capture.template_key}` : ''"
    :style="pageStyle"
  >
    <div v-if="loading" class="capture-state">Carregando...</div>
    <div v-else-if="!capture" class="capture-state capture-state--error">
      <h1>Página indisponível</h1>
      <p>{{ errorMessage }}</p>
    </div>

    <iframe
      v-else-if="isCustom"
      ref="customFrame"
      title="Formulário de captação"
      class="custom-frame"
      sandbox="allow-forms allow-scripts"
      :srcdoc="customDocument"
    />

    <div v-else class="template-shell">
      <section class="capture-hero">
        <div class="capture-hero__content">
          <p class="eyebrow">{{ content.eyebrow }}</p>
          <h1>{{ content.headline }}</h1>
          <p class="description">{{ content.description }}</p>
          <div class="trust-row">
            <span>✓ Análise rápida</span><span>✓ Dados protegidos</span>
          </div>
        </div>
      </section>

      <section class="form-region">
        <div class="form-card">
          <div v-if="showInteraction" class="interaction-card">
            <div class="interaction-marker">{{ interaction.marker }}</div>
            <h2>{{ interaction.title }}</h2>
            <p>{{ interaction.description }}</p>
            <button type="button" @click="interactionDone = true">
              {{ interaction.action }}
            </button>
          </div>

          <div v-else-if="submitted" class="success-card" aria-live="polite">
            <span>✓</span>
            <h2>Dados recebidos</h2>
            <p>{{ content.success_message }}</p>
            <small v-if="conversationId"
              >Atendimento #{{ conversationId }}</small
            >
          </div>

          <form v-else @submit.prevent="submitLead()">
            <h2>{{ content.form_title }}</h2>
            <label v-for="field in enabledFields" :key="field.key">
              {{ field.label }}
              <input
                v-model="form[field.key]"
                :type="field.type"
                :autocomplete="field.autocomplete"
                :required="field.required"
              />
            </label>
            <input
              v-model="form.website"
              name="website"
              tabindex="-1"
              autocomplete="off"
              class="honeypot"
            />
            <p v-if="errorMessage" class="form-error" aria-live="polite">
              {{ errorMessage }}
            </p>
            <button type="submit" :disabled="submitting">
              {{ submitting ? 'Enviando...' : content.button_label }}
            </button>
            <small
              >Seus dados serão enviados com segurança para atendimento.</small
            >
          </form>
        </div>
      </section>
    </div>
  </main>
</template>

<style scoped>
.capture-page {
  min-height: 100vh;
  background: var(--capture-background);
  color: var(--capture-text);
  font-family: Inter, ui-sans-serif, system-ui, sans-serif;
}
.capture-state {
  display: grid;
  min-height: 100vh;
  place-content: center;
  padding: 24px;
  text-align: center;
}
.capture-state--error h1 {
  margin-bottom: 8px;
  font-size: 28px;
}
.custom-frame {
  display: block;
  width: 100%;
  min-height: 100vh;
  border: 0;
  background: var(--capture-background);
}
.template-shell {
  min-height: 100vh;
}
.capture-hero {
  display: flex;
  min-height: 430px;
  align-items: center;
  padding: 64px max(24px, calc((100vw - 1180px) / 2));
  background: linear-gradient(
    135deg,
    var(--capture-secondary),
    var(--capture-primary)
  );
  color: white;
}
.capture-hero__content {
  max-width: 650px;
}
.eyebrow {
  margin: 0 0 16px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  opacity: 0.75;
}
.capture-hero h1 {
  margin: 0 0 18px;
  font-size: clamp(40px, 5vw, 68px);
  line-height: 1.02;
  letter-spacing: -0.04em;
}
.description {
  max-width: 560px;
  margin: 0;
  font-size: 18px;
  line-height: 1.65;
  opacity: 0.82;
}
.trust-row {
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  margin-top: 28px;
  font-size: 13px;
  font-weight: 700;
}
.form-region {
  padding: 40px max(24px, calc((100vw - 1180px) / 2));
}
.form-card {
  width: min(100%, 480px);
  margin: -160px 0 40px auto;
  padding: 30px;
  border-radius: 22px;
  background: var(--capture-card);
  box-shadow: 0 24px 70px #0f172a2e;
}
.form-card h2 {
  margin: 0 0 22px;
  font-size: 23px;
}
.form-card form,
.form-card label {
  display: grid;
}
.form-card form {
  gap: 14px;
}
.form-card label {
  gap: 7px;
  font-size: 13px;
  font-weight: 700;
}
.form-card input {
  height: 48px;
  border: 1px solid #cbd5e1;
  border-radius: 11px;
  padding: 0 14px;
  background: white;
  color: #0f172a;
  font: inherit;
}
.form-card input:focus {
  border-color: var(--capture-primary);
  outline: 3px solid color-mix(in srgb, var(--capture-primary) 18%, transparent);
}
.form-card button {
  min-height: 50px;
  border: 0;
  border-radius: 11px;
  background: var(--capture-primary);
  color: white;
  font-weight: 800;
  cursor: pointer;
}
.form-card button:disabled {
  cursor: wait;
  opacity: 0.65;
}
.form-card small {
  color: #64748b;
  text-align: center;
}
.honeypot {
  position: absolute !important;
  left: -10000px !important;
}
.form-error {
  margin: 0;
  color: #b91c1c;
  font-size: 13px;
}
.interaction-card,
.success-card {
  text-align: center;
}
.interaction-card p,
.success-card p {
  color: #64748b;
  line-height: 1.6;
}
.interaction-card button {
  width: 100%;
}
.interaction-marker {
  display: grid;
  width: 112px;
  height: 112px;
  margin: 0 auto 22px;
  place-content: center;
  border: 8px solid color-mix(in srgb, var(--capture-primary) 15%, white);
  border-radius: 50%;
  background: var(--capture-primary);
  color: white;
  font-weight: 900;
}
.success-card > span {
  display: grid;
  width: 64px;
  height: 64px;
  margin: 0 auto 18px;
  place-content: center;
  border-radius: 50%;
  background: #dcfce7;
  color: #15803d;
  font-size: 30px;
}
.capture-page--centered .capture-hero {
  min-height: 430px;
  justify-content: center;
  text-align: center;
}
.capture-page--centered .capture-hero__content {
  max-width: 760px;
}
.capture-page--centered .description {
  margin-inline: auto;
}
.capture-page--centered .trust-row {
  justify-content: center;
}
.capture-page--centered .form-card {
  margin: -120px auto 40px;
}
.capture-page--minimal .capture-hero {
  min-height: auto;
  padding-block: 70px;
  background: var(--capture-background);
  color: var(--capture-text);
}
.capture-page--minimal .capture-hero h1 {
  font-size: clamp(36px, 5vw, 58px);
}
.capture-page--minimal .trust-row {
  display: none;
}
.capture-page--minimal .form-card {
  margin: 0 auto 40px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 16px 40px #0f172a12;
}
@media (max-width: 850px) {
  .capture-hero {
    min-height: 390px;
    align-items: flex-start;
    padding-top: 58px;
    text-align: center;
  }
  .capture-hero__content {
    margin: auto;
  }
  .trust-row {
    justify-content: center;
  }
  .form-region {
    padding-top: 0;
  }
  .form-card {
    margin: -80px auto 30px;
  }
  .capture-page--minimal .form-card {
    margin-top: 28px;
  }
}
</style>
